/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { useAuth } from '../components/authentication';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  EmailInput,
  NewPasswordInputs,
  PasswordInput,
  usePasswordInputs,
} from '../components/authentication/AuthComponent';
import Form from '../components/Form';
import { useMutation } from 'react-query';
import { networkWithErrorConvert } from '../api';
import { VerifyEmailCommon } from './VerifyEmail';

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}));

const useCheckPassword = (setOpen) =>
  function useCheckPasswordBinded() {
    const { username } = useAuth();
    return useMutation(
      (event) => {
        const data = new FormData(event.target);
        return networkWithErrorConvert({
          method: 'post',
          url: '/v1/signin',
          auth: {
            username,
            password: data.get('password'),
          },
        });
      },
      {
        onSuccess: () => {
          setOpen(false);
        },
      }
    );
  };

function ConfirmPasswordDialog({ open, setOpen }) {
  const navigate = useNavigate();
  const { username } = useAuth();
  return (
    <Dialog maxWidth="xs" open={open}>
      <DialogTitle>Please confirm password of {username}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          We must verify your identity to make any user changes
        </DialogContentText>
        <Form
          Inputs={<PasswordInput autoComplete="current-password" />}
          SubmitButtonTitle="Verify"
          mutation={useCheckPassword(setOpen)}
        />
      </DialogContent>
      <DialogActions>
        <Button
          data-cy="cancel-verification"
          onClick={() => {
            navigate('/');
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function useChangePassword(dataOptions) {
  return useMutation((event) => {
    const data = new FormData(event.target);
    return networkWithErrorConvert({
      method: 'post',
      url: '/v1/user/password',
      data: {
        password: data.get('password'),
        ...dataOptions,
      },
    });
  });
}

function AccordionContentContainer({ children }) {
  return (
    <Box
      sx={{
        margin: 'auto',
        width: 400,
        p: 2,
        backgroundColor: (theme) => theme.palette.background.default,
      }}
    >
      {children}
    </Box>
  );
}

export function NewPassword({
  mutation = useChangePassword,
  SuccessComponent = (
    <Typography>The password is changed successfully!</Typography>
  ),
}) {
  const passwordInputs = usePasswordInputs();
  return (
    <Form
      SuccessMessage={SuccessComponent}
      Inputs={
        <NewPasswordInputs
          newPasswordLabel="New password"
          {...passwordInputs}
        />
      }
      SubmitButtonTitle="Change password"
      mutation={mutation}
      validate={passwordInputs.validate}
    />
  );
}

function useChangeEmail() {
  return useMutation((event) => {
    const data = new FormData(event.target);
    return networkWithErrorConvert({
      method: 'post',
      url: '/v1/user/email',
      data: {
        email: data.get('username'),
      },
    });
  });
}

function useVerifyEmail() {
  return useMutation((event) => {
    const data = new FormData(event.target);
    return networkWithErrorConvert({
      method: 'post',
      url: '/v1/user/verify-email',
      data: {
        otp: data.get('otp'),
      },
    });
  });
}

function NewEmail() {
  const changeEmail = useChangeEmail();
  const verifyEmail = useVerifyEmail();
  return (
    <>
      <Form
        SuccessMessage={
          <Typography sx={{ pb: 2 }}>
            Verification code is sent to your email. Please check your inbox and
            fill the "Verification code" input
          </Typography>
        }
        Inputs={<EmailInput />}
        SubmitButtonTitle="Change email"
        mutation={() => ({
          ...changeEmail,
          mutate: (...args) => {
            verifyEmail.reset();
            changeEmail.mutate(...args);
          },
        })}
      />
      <Divider />
      <VerifyEmailCommon
        mutation={() => verifyEmail}
        Component={Form}
        disabled={!changeEmail.isSuccess || verifyEmail.isSuccess}
      />
    </>
  );
}

function useDeleteUser() {
  const { deleteUser } = useAuth();
  return useMutation(deleteUser);
}

function DeleteUser() {
  const { username } = useAuth();
  const emailRef = React.createRef();
  const [error, setError] = React.useState('');
  return (
    <Box>
      <Typography
        color="error"
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
        }}
      >
        <WarningIcon /> Danger zone! This can't be reverted.
        <WarningIcon />
      </Typography>
      <Typography align="left" sx={{ pt: 2 }}>
        Please type your email to confirm the action
      </Typography>
      <Form
        SuccessMessage={<Navigate to="/" />}
        Inputs={
          <EmailInput
            ref={emailRef}
            autoFocus={false}
            id="confirm-email"
            error={Boolean(error)}
            helperText={error}
          />
        }
        submitButtonProps={{
          color: 'error',
        }}
        SubmitButtonTitle="Delete user"
        validate={() => {
          if (username !== emailRef.current.value) {
            setError("The input email doesn't match the current email");
            return false;
          }
          return true;
        }}
        mutation={useDeleteUser}
      />
    </Box>
  );
}

const AccordionContent = [
  { Summary: 'Change password', Details: NewPassword },
  { Summary: 'Change email', Details: NewEmail },
  { Summary: 'Delete user', Details: DeleteUser },
];

const transitionProps = { unmountOnExit: true };

export default function UserManagement() {
  const { signedByPassword } = useAuth();
  const [open, setOpen] = React.useState(!signedByPassword);
  return (
    <Box>
      <ConfirmPasswordDialog open={open} setOpen={setOpen} />
      {AccordionContent.map(({ Summary, Details }) => (
        <Accordion
          key={Summary}
          disabled={open}
          TransitionProps={transitionProps}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{Summary}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AccordionContentContainer>
              <Details />
            </AccordionContentContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
